import { addApolloState, initializeApollo } from '@/utils/apolloClient'
import { getHomePageData } from '@/api/strapi/homePage'
import { IHomePage } from '@/types/strapi/homePage.interface'
import { ReactNode } from 'react'
import type { NextPage, GetStaticPropsContext } from 'next'
import {
  Box,
  Button,
  Center,
  Text,
  Image,
  Flex,
  Link,
  Icon,
  VStack,
} from '@chakra-ui/react'
import { ArrowForwardIcon } from '@chakra-ui/icons'
import NextLink from 'next/link'
import { useTranslations } from 'next-intl'
import SeoHead from '@/components/seo-head'
import { CompassArrowIcon } from '@/components/icons/CompassArrowIcon'

type Props = {
  homePageData: IHomePage
}

const AcceleratedByCompassPage: NextPage<Props> = ({ homePageData }) => {
  const t = useTranslations()

  const signature_services = [
    {
      key: 'procurement-and-construction',
      image_url: '/img/acc_by_compass_img2.png',
    },
    {
      key: 'operations-and-management',
      image_url: '/img/acc_by_compass_img3.png',
    },
    {
      key: 'energy-optimization',
      image_url: '/img/acc_by_compass_img4.png',
    },
    {
      key: 'monetizing-infrastracture',
      image_url: '/img/acc_by_compass_img5.png',
    },
  ]

  return (
    <>
      <SeoHead
        title={t('accelerated.seo.title')}
        breadcrumbTitle={t('accelerated.seo.breadcrumb')}
        description={t('accelerated.seo.description')}
      />
      <Box bg="black.400" color="white">
        <Center
          flexDirection="column"
          height={{ base: 'auto', xl: '600px' }}
          padding={{
            base: '40px 16px',
            md: '8.046875vw 8.59375vw',
            xl: '103px 110px',
          }}
          textAlign="center"
          color="white"
        >
          <Icon as={CompassArrowIcon} width={'52px'} height={'52px'} />
          <VStack spacing={'24px'}>
            <Text fontSize={'32px'} as="h2">
              {t.rich('accelerated.title1', {
                styled: (children: ReactNode) => (
                  <Text as="span" variant="brandGradient">
                    {children}
                  </Text>
                ),
              })}
            </Text>
            <Text
              fontSize={'48px'}
              width={{ base: '100%', md: '696px' }}
              lineHeight="100%"
              as="h1"
            >
              {t('accelerated.title2')}
            </Text>
            <Text
              width={{ base: '100%', md: '820px' }}
              fontSize={'24px'}
              lineHeight={'32px'}
            >
              {t('accelerated.description')}
            </Text>
            <NextLink href="/accelerated/get-started" passHref>
              <Link>
                <Button
                  width={{ base: '100%', md: '282px' }}
                  borderRadius="none"
                  variant="brandGradient"
                >
                  {t('accelerated.get-started')}
                </Button>
              </Link>
            </NextLink>
          </VStack>
        </Center>
        <Center
          backgroundColor="black"
          height={{ base: 'auto', md: '600px' }}
          padding={{ base: '0 0 40px 0', md: 'unset' }}
        >
          <VStack spacing="16px">
            <Box
              width={{ base: '100%', md: '70%', xl: '866px' }}
              height={'347px'}
            >
              <Image
                src="/img/acc_by_compass_img1.png"
                alt={t('accelerated.title2')}
                width="100%"
                height="100%"
                objectFit="cover"
                objectPosition="left"
              />
            </Box>
            <Text
              textAlign="center"
              fontSize={{ base: '24px', md: '32px' }}
              lineHeight={{ base: '', md: '44px' }}
              maxWidth={{ xl: '1140px' }}
              padding={{ base: '0 16px', md: 'unset' }}
            >
              {t('accelerated.description2')}
            </Text>
          </VStack>
        </Center>
        <Center>
          <VStack
            justifyContent="center"
            spacing={{ base: '32px', md: '56px' }}
            minHeight={{ base: '', md: '977px' }}
            height="auto"
            width={{ base: '100%', md: '85%', '2xl': '1150px' }}
            py={10}
            px={{ base: '16px', md: 'unset' }}
          >
            <Text
              fontWeight="500"
              fontSize={'40px'}
              lineHeight={{ base: '', md: '44px' }}
            >
              {t('accelerated.signature-services')}
            </Text>
            <Flex
              flexWrap="wrap"
              justifyContent={'center'}
              alignItems="stretch"
              gridGap={{ base: '16px', md: '56px' }}
            >
              {signature_services.map((service) => {
                return (
                  <VStack
                    key={`wrap-${service.key}`}
                    textAlign="center"
                    spacing="16px"
                    maxWidth={{ md: '539px' }}
                    marginBottom={{ base: '16px', md: '24px' }}
                  >
                    <Box width={'100%'} height={'221px'} borderRadius="8px">
                      <Image
                        src={service.image_url}
                        alt={t(`accelerated.${service.key}.title`)}
                        width="100%"
                        height="100%"
                        objectFit="cover"
                        borderRadius="8px"
                      />
                    </Box>
                    <Text fontWeight="600" fontSize={'32px'}>
                      {t(`accelerated.${service.key}.title`)}
                    </Text>
                    <Text fontSize={'16px'}>
                      {t(`accelerated.${service.key}.description`)}
                    </Text>
                    <NextLink href={`/accelerated/${service.key}`} passHref>
                      <Link>
                        <Text
                          color="brand.600"
                          fontSize={{ base: '', md: '16px' }}
                        >
                          {t(`accelerated.learn-more`)}
                          &nbsp;
                          <ArrowForwardIcon />
                        </Text>
                      </Link>
                    </NextLink>
                  </VStack>
                )
              })}
            </Flex>
          </VStack>
        </Center>
        <Center
          backgroundColor="black"
          height={{ base: 'auto', xl: '600px' }}
          padding={{ base: '16px', xl: 'unset' }}
        >
          <Flex
            background="white"
            flexDirection={{ base: 'column', xl: 'row' }}
          >
            <Image
              src="/img/acc_by_compass_img6.png"
              alt={t(`accelerated.ready-to-accelerate`)}
              width={{ base: '100%', xl: '484px' }}
              height={{ base: '249px', md: '336px' }}
            />
            <VStack
              textAlign="center"
              justifyContent="center"
              mx={{ base: '16px', md: '95px' }}
              maxWidth={{ base: 'none', md: '462px' }}
              spacing={'35px'}
              py={{ base: '32px', xl: 'unset' }}
            >
              <Text color="black" fontSize={'32px'}>
                {t(`accelerated.ready-to-accelerate`)}
              </Text>
              <NextLink href="/accelerated/get-started" passHref>
                <Link>
                  <Button
                    background="brand.600"
                    color="white"
                    borderRadius="none"
                    width={{ base: '100%', md: '272px' }}
                    height={'50px'}
                    _hover={{
                      background: 'brand.600',
                      opacity: 0.8,
                    }}
                  >
                    {t(`accelerated.get-started-today`)}
                  </Button>
                </Link>
              </NextLink>
              <Text color="black" fontSize={'16px'}>
                {t.rich('accelerated.have-questions', {
                  styled: (children: ReactNode) => (
                    <NextLink
                      href="/accelerated/get-started#specialists"
                      passHref
                    >
                      <Link>
                        <Text as="span" color="brand.600">
                          {children}
                        </Text>
                      </Link>
                    </NextLink>
                  ),
                })}
              </Text>
            </VStack>
          </Flex>
        </Center>
        <Center>
          <VStack textAlign="center" py={'40px'} spacing="30px">
            <Text
              fontWeight="500"
              lineHeight={{ base: '40px', md: '44px' }}
              fontSize={{ base: '32px', md: '40px' }}
              maxWidth={{ base: '95%', md: '70%' }}
            >
              {t(`accelerated.as-your-trusted-strategic-partner`)}
            </Text>
            <Flex
              w="full"
              justify={'space-between'}
              flexDirection={{ base: 'column', sm: 'row' }}
              gridGap={{ base: '30px', md: 'initial' }}
              flexWrap={{ base: 'wrap', lg: 'unset' }}
            >
              {homePageData?.statistics?.map((stat, i: number) => (
                <Flex
                  key={`stat-${i}`}
                  flexDirection={'column'}
                  align={'center'}
                  justify={'flex-start'}
                  w="full"
                >
                  {stat.iconImage?.data?.attributes?.url && (
                    <Box boxSize={{ base: '64px', md: '36px', xl: '46px' }}>
                      <Image
                        height="100%"
                        width="100%"
                        objectFit="contain"
                        src={stat.iconImage?.data?.attributes?.url}
                      />
                    </Box>
                  )}
                  {stat?.description && (
                    <Text
                      fontSize={{ base: '40px', md: '30px', xl: '44px' }}
                      lineHeight={{ base: '150%', md: '40px', xl: '53px' }}
                      fontWeight={'semibold'}
                      color={'white'}
                      pt={{ base: '15px', md: '20px' }}
                      align={'start'}
                      justifyItems={'flex-end'}
                    >
                      {stat?.description}
                    </Text>
                  )}
                  {stat?.title && (
                    <Text
                      fontSize={{ base: '15px', md: '12px', xl: '15px' }}
                      lineHeight={{ base: '150%', md: '17px', xl: '19px' }}
                      fontWeight={'semibold'}
                      color={'gray.850'}
                      w="full"
                      pt={{ base: '8px', md: '10px', xl: '13px' }}
                      align={'center'}
                    >
                      {stat?.title}
                    </Text>
                  )}
                </Flex>
              ))}
            </Flex>
          </VStack>
        </Center>
      </Box>
    </>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  const apolloClient = initializeApollo()
  const { data } = await apolloClient.query({
    query: getHomePageData,
    variables: {
      locale,
    },
    context: {
      clientName: 'strapi',
    },
  })

  if (!data) {
    return addApolloState(apolloClient, {
      notFound: true,
    })
  }

  const homePageData = data.homePage?.data?.attributes || {}

  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
      homePageData,
    },
  }
}

export default AcceleratedByCompassPage
